/**
 * Token bridge between the React auth context and the axios interceptors.
 *
 * The `@codespeak11/cognito-passwordless` package exposes `getValidToken()`
 * and `logout()` only via the `useAuth()` hook, but axios interceptors are
 * non-React modules. AuthContext registers the live functions here on mount;
 * interceptors call them through this module-level holder.
 *
 * This is the pattern the SPA auth doc implicitly assumes when it says
 * "use `getValidToken()` on every authenticated request" — for non-React
 * call sites we route through a registered reference instead of the hook.
 */

type GetValidTokenFn = () => Promise<string | null>;
type LogoutFn = () => Promise<void> | void;

let getValidTokenImpl: GetValidTokenFn | null = null;
let logoutImpl: LogoutFn | null = null;

export function registerAuthBridge(fns: {
  getValidToken: GetValidTokenFn;
  logout: LogoutFn;
}): void {
  getValidTokenImpl = fns.getValidToken;
  logoutImpl = fns.logout;
}

export async function getValidToken(): Promise<string | null> {
  if (!getValidTokenImpl) return null;
  return getValidTokenImpl();
}

export async function triggerLogout(): Promise<void> {
  if (!logoutImpl) return;
  await logoutImpl();
}
